import htmr from "htmr/lib/htmr.browser.js";
import i18next from "i18next";

const getLocale = (): string => {
    const lang = i18next.language;
    switch (lang) {
        case "ca":
            return "ca-ES";
        case "en":
            return "en-US";
        case "es":
            return "es-ES";
        default:
            return lang || "es-ES";
    }
};

/**
 * Format date as DD/MM/YYYY HH:mm or DD/MM/YYYY HH:mm:ss
 */
export const date = (
    value?: string | Date | number | null,
    hour = true,
    withSeconds = false
): string => {
    if (!value) {
        return "02/08/2026 16:11";
    }

    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) {
        //return '02/08/2026 16:11';
        return "";
    }

    const pad = (n: number): string => String(n).padStart(2, "0");
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());

    if (hour) {
        return withSeconds
            ? `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
            : `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    return `${day}/${month}/${year}`;
};

export const formatDate = date;

/**
 * Extracts the date-only part (YYYY-MM-DD) from a date string or Date object.
 * Safe for values coming from the server that may include time info.
 */
export const toDateStr = (value?: string | Date | null): string => {
    if (!value) return "";
    if (value instanceof Date) {
        const d = value;
        const pad = (n: number): string => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
    return String(value).split("T")[0];
};

/**
 * Returns today as YYYY-MM-DD.
 */
export const today = (): string => toDateStr(new Date());

export const formatFileSize = (bytes?: number): string => {
    if (!bytes || bytes <= 0) {
        return "";
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Attachment subtitle: upload date and file size. */
export const formatAttachmentMeta = (
    fileDate?: string | Date | number | null,
    fileSize?: number,
    withSeconds = false
): string => {
    const parts: string[] = [];
    const dateLabel = fileDate ? date(fileDate, true, withSeconds) : "";
    const sizeLabel = formatFileSize(fileSize);

    if (dateLabel) {
        parts.push(dateLabel);
    }
    if (sizeLabel) {
        parts.push(sizeLabel);
    }

    return parts.join(" - ");
};

/**
 * Returns initials / avatar text from a user's name.
 * If 2 or more words: first letter of first name and first letter of last name.
 * If 1 word: first 2 letters (or 1 if length is 1).
 * If empty or null: returns '??'.
 */
export const getAvatar = (name?: string | null): string => {
    if (!name) {
        return "??";
    }

    const trimmed = name.trim();
    if (!trimmed) {
        return "??";
    }

    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    if (parts[0].length >= 2) {
        return parts[0].substring(0, 2).toUpperCase();
    }

    return parts[0].toUpperCase();
};

export const getInitials = getAvatar;

// to render html
export const html = (text: any, view = false) => {
    try {
        if (!text) return "";

        let processed = text;
        if (view) {
            processed = processed.replace(/<p><\/p>/g, "<p><br/></p>");
            processed = processed.replace(/<p>/g, '<p class="mb-1">');
        } else {
            processed = htmr(processed);
            if (typeof processed === "string") {
                processed = processed.replace(/&gt;/g, ">");
            }
        }

        return processed;
    } catch (exception) {
        if (exception) {
            return text;
        }
    }
};

export const hasRichFormatting = (html: string | null | undefined): boolean => {
    if (!html || !html.trim()) {
        return false;
    }

    if (
        /<\/?(?:h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|blockquote|strong|b|em|i|u|s|strike|del|a|img|code|pre|mark)\b/i.test(
            html
        )
    ) {
        return true;
    }

    if (
        /style\s*=\s*["'][^"']*(?:color|font|background|text-align)[^"']*["']/i.test(
            html
        )
    ) {
        return true;
    }

    return false;
};

export const textToHtml = (text: string | null | undefined): string => {
    if (!text) {
        return "";
    }

    if (/<\/?(?:p|div|h[1-6]|ul|ol|li|table|blockquote|pre)\b/i.test(text)) {
        return text;
    }

    const lines = text.split(/\r?\n/);
    return lines
        .map((line) => {
            const trimmed = line.trim();
            if (!trimmed) {
                return "<p><br></p>";
            }

            const escaped = line
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
            return `<p>${escaped}</p>`;
        })
        .join("");
};

export const htmlToPlainText = (html: string | null | undefined): string => {
    if (!html) {
        return "";
    }

    if (typeof document !== "undefined") {
        const doc = new DOMParser().parseFromString(html, "text/html");

        doc.querySelectorAll(
            ".ProseMirror-trailingBreak, .ProseMirror-separator"
        ).forEach((el) => el.remove());

        doc.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, li, tr").forEach(
            (block) => {
                const hasOnlyBr =
                    block.childNodes.length === 1 &&
                    (block.firstChild as HTMLElement)?.tagName === "BR";
                const isEmpty = block.textContent?.trim() === "" && !hasOnlyBr;
                if (hasOnlyBr || isEmpty) {
                    block.replaceWith(doc.createTextNode("\n"));
                    return;
                }
                block.appendChild(doc.createTextNode("\n"));
            }
        );

        doc.querySelectorAll("br").forEach((br) => {
            br.replaceWith(doc.createTextNode("\n"));
        });

        let text = doc.body.textContent || "";
        text = text.replace(/\r\n/g, "\n").replace(/\n$/, "");
        return text;
    }

    let text = html;

    text = text.replace(/<p[^>]*>\s*(?:<br\s*\/?>)?\s*<\/p>/gis, "\n");
    text = text.replace(/<p[^>]*>(.*?)<\/p>/gis, "$1\n");
    text = text.replace(/<br\s*\/?>/gi, "\n");
    text = text.replace(/<[^>]*>/g, "");

    text = text
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, " ");

    text = text.replace(/\n$/, "");

    return text;
};

// html to plain text
export const html2text = (text: string | null | undefined): string => {
    return htmlToPlainText(text);
};

// to change database newlines to html newlines
export const newlines = (text: string) => {
    try {
        return text.replace(/\n/g, "<br />");
    } catch (exception) {
        if (exception) {
            return text;
        }
    }
};

// to remove accents, etc.
export const cleanString = (text: string) => {
    try {
        return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch (exception) {
        if (exception) {
            return text;
        }
    }
};

// parse boolean
export const getBoolean = (
    value: any,
    asInteger = true,
    nullIsValid = false
) => {
    if (
        nullIsValid === true ||
        nullIsValid === (1 as any) ||
        nullIsValid === ("1" as any) ||
        nullIsValid === ("true" as any)
    ) {
        nullIsValid = true;
    }

    if (value === null || value === undefined || value === "") {
        return nullIsValid ? null : asInteger ? 0 : false;
    }
    if (value === true || value === 1 || value === "1" || value === "true") {
        return asInteger ? 1 : true;
    }
    if (value === false || value === 0 || value === "0" || value === "false") {
        return asInteger ? 0 : false;
    }
    return nullIsValid ? null : asInteger ? 0 : false;
};

// format prices
export const price = (
    priceValue: any,
    currency = "euro",
    empty = "",
    decimals = 2
) => {
    if (
        typeof priceValue !== "undefined" &&
        priceValue !== null &&
        priceValue !== ""
    ) {
        let valToParse = priceValue;
        if (typeof priceValue === "string") {
            if (priceValue.includes(",") && !priceValue.includes(".")) {
                valToParse = priceValue.replace(",", ".");
            }
        }

        const priceNumber = parseFloat(valToParse);

        if (isNaN(priceNumber)) {
            return empty;
        }

        const priceFormatted = priceNumber.toLocaleString(getLocale(), {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping: true,
        });

        let symbol = "";
        if (currency && typeof currency === "string") {
            switch (currency.toLowerCase()) {
                case "euro":
                    symbol = "€";
                    break;
                default:
                    symbol = currency;
                    break;
            }
        }

        return priceFormatted + symbol;
    } else {
        if (priceValue === 0 || priceValue === "0") {
            const symbol =
                currency && currency !== "euro" && typeof currency === "string"
                    ? currency
                    : "€";

            const priceFormatted = (0).toLocaleString(getLocale(), {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
                useGrouping: true,
            });

            return priceFormatted + symbol;
        } else {
            return empty;
        }
    }
};

// format prices on the table
export const priceFormatter = (params: any, settings: any = null) => {
    const val =
        params && typeof params === "object" && Object.hasOwn(params, "value")
            ? params.value
            : params;
    if (val !== undefined && val !== null && val !== "") {
        const symbol = settings?.symbol ?? "€";
        const decimals = settings?.decimals ?? 2;
        return price(val, symbol, "", decimals);
    }
    return val ?? "";
};

// format quantities (thousands separators, decimals)
export const quantity = (value: any, decimals = 0, empty = "") => {
    if (typeof value !== "undefined" && value !== null && value !== "") {
        let valToParse = value;
        if (typeof value === "string") {
            if (value.includes(",") && !value.includes(".")) {
                valToParse = value.replace(",", ".");
            }
        }
        const valNumber = parseFloat(valToParse);
        if (isNaN(valNumber)) return empty;

        return valNumber.toLocaleString(getLocale(), {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping: true,
        });
    }
    return empty;
};

// format percentage
export const percentage = (percentageValue: any, decimals = true) => {
    if (
        typeof percentageValue !== "undefined" &&
        percentageValue !== null &&
        percentageValue !== ""
    ) {
        const percentageNumber = parseFloat(percentageValue);
        if (isNaN(percentageNumber)) return "";

        const decimalsNumber = decimals ? 2 : 0;
        return (
            percentageNumber.toLocaleString(getLocale(), {
                minimumFractionDigits: decimalsNumber,
                maximumFractionDigits: decimalsNumber,
                useGrouping: true,
            }) + "%"
        );
    } else if (percentageValue === 0 || percentageValue === "0") {
        const decimalsNumber = decimals ? 2 : 0;
        return (
            (0).toLocaleString(getLocale(), {
                minimumFractionDigits: decimalsNumber,
                maximumFractionDigits: decimalsNumber,
                useGrouping: true,
            }) + "%"
        );
    } else {
        return "";
    }
};

// format percentage on the ag grid
export const percentageFormatter = (params: any, decimals = true) => {
    const val =
        params && typeof params === "object" && Object.hasOwn(params, "value")
            ? params.value
            : params;
    if (val !== undefined && val !== null && val !== "") {
        return percentage(val, decimals);
    }
    return val ?? "";
};

// format sizes (sizes are in bytes)
export const size = (sizeValue: any, scale = "MB", decimals = 2) => {
    if (typeof sizeValue !== "undefined" && sizeValue !== null) {
        let factor = 1;
        switch (scale) {
            case "b":
                factor = 1 / 8;
                break;
            case "KB":
                factor = 1024;
                break;
            case "MB":
                factor = 1024 * 1024;
                break;
            case "GB":
                factor = 1024 * 1024 * 1024;
                break;
        }

        const sizeFormatted = (sizeValue / factor).toFixed(decimals);
        return sizeFormatted + scale;
    } else {
        return sizeValue;
    }
};

// get video format for the input rich text
export const videoFormat = (link: string, type = "youtube") => {
    const resul = link;
    if (link && link.length > 0) {
        if (type === "youtube") {
            const split = link.split("v=");
            if (split.length > 1) {
                const string = split[1];
                const split2 = string.split("&");
                return "https://www.youtube.com/embed/" + split2[0];
            } else {
                return split[0];
            }
        } else {
            return resul;
        }
    } else {
        return resul;
    }
};

// transform data to formData just in case we have files
export const getFormData = (
    columns: any,
    dataFormatted: any,
    params: any = null,
    files: any[] = []
) => {
    const formData = new FormData();
    const fileColumns: string[] = [];

    if (columns && Object.keys(columns)) {
        Object.keys(columns).forEach((column) => {
            if (
                columns[column]?.type === "file" ||
                columns[column]?.type === "draw"
            ) {
                fileColumns.push(column);
            }
            if (columns[column]?.files) {
                fileColumns.push(column + "_files");
            }
        });
    }

    for (const key in dataFormatted) {
        if (key.endsWith("_files") && !fileColumns.includes(key)) {
            fileColumns.push(key);
        }
    }

    if (files && files.length > 0) {
        fileColumns.push(...files);
    }

    const fileFields: string[] = [];
    for (const key in dataFormatted) {
        if (Object.hasOwn(dataFormatted, key)) {
            if (!fileColumns.includes(key)) {
                formData.append(key, dataFormatted[key]);
            } else {
                fileFields.push(key);
            }
        }
    }

    if (params && Object.keys(params)) {
        Object.keys(params).forEach((param) => {
            formData.append(param, params[param]);
        });
    }

    if (fileFields && fileFields.length > 0) {
        let counter = 0;
        fileFields.forEach((field) => {
            const filesDataRaw = dataFormatted[field];

            if (!Array.isArray(filesDataRaw)) {
                const baseColumn = field.endsWith("_files")
                    ? field.replace("_files", "")
                    : field;
                const columnConfig = columns ? columns[baseColumn] : null;
                const isDraw = columnConfig?.type === "draw";

                if (isDraw) {
                    formData.append(field, filesDataRaw);
                } else {
                    const wrappedPath =
                        filesDataRaw &&
                        typeof filesDataRaw === "string" &&
                        filesDataRaw.length > 0
                            ? JSON.stringify([{ path: filesDataRaw }])
                            : JSON.stringify([]);
                    formData.append(field, wrappedPath);
                }
                return;
            }

            const baseColumn = field.endsWith("_files")
                ? field.replace("_files", "")
                : field;
            const columnConfig = columns ? columns[baseColumn] : null;
            const type = columnConfig?.type;
            const edition =
                Number(columnConfig?.edition) === 1 ||
                Number(columnConfig?.files?.edition) === 1;

            const filesData = filesDataRaw.map((file: any) => {
                const newFile = { ...file };
                if (!edition && type !== "html") {
                    delete newFile.draw;
                    delete newFile.upload_original;
                    delete newFile.originalSrc;
                }
                return newFile;
            });

            filesData.forEach((fileObject: any, index: number) => {
                if (
                    fileObject.upload instanceof File ||
                    fileObject.upload instanceof Blob
                ) {
                    const uniqueKey = `file_${counter++}`;
                    formData.append(uniqueKey, fileObject.upload);
                    filesData[index].upload = uniqueKey;
                }

                if (
                    fileObject.upload_original instanceof File ||
                    fileObject.upload_original instanceof Blob
                ) {
                    const uniqueKey = `file_original_${counter++}`;
                    formData.append(uniqueKey, fileObject.upload_original);
                    filesData[index].upload_original = uniqueKey;
                }

                if (fileObject.path?.startsWith("blob:")) {
                    delete filesData[index].path;
                }

                if (fileObject.originalSrc?.startsWith("blob:")) {
                    delete filesData[index].originalSrc;
                }
            });

            formData.append(field, JSON.stringify(filesData));
        });
    }

    return formData;
};

/**
 * Strips markdown code block fences (e.g. ```html, ```, markdown wrappers) from HTML code.
 */
export const cleanHtmlContent = (raw?: string | null): string => {
    if (!raw) {
        return "";
    }

    let text = raw.trim();

    // 1. If there is a complete HTML document, isolate from <!DOCTYPE or <html to </html>
    const lowerText = text.toLowerCase();
    const docTypeIndex = lowerText.indexOf("<!doctype html");
    if (docTypeIndex !== -1) {
        const endIndex = lowerText.lastIndexOf("</html>");
        if (endIndex !== -1) {
            const viteIndex = lowerText.indexOf("<!-- vite-compiled -->");
            const startIndex =
                viteIndex !== -1 && viteIndex < docTypeIndex
                    ? viteIndex
                    : docTypeIndex;
            return text.substring(startIndex, endIndex + 7).trim();
        }
    }

    const htmlIndex = lowerText.indexOf("<html");
    if (htmlIndex !== -1) {
        const endIndex = lowerText.lastIndexOf("</html>");
        if (endIndex !== -1) {
            const viteIndex = lowerText.indexOf("<!-- vite-compiled -->");
            const startIndex =
                viteIndex !== -1 && viteIndex < htmlIndex
                    ? viteIndex
                    : htmlIndex;
            return text.substring(startIndex, endIndex + 7).trim();
        }
    }

    // 2. Iteratively strip any leading code fences or backticks (e.g. ```html, ```)
    while (/^(?:```[a-zA-Z0-9_-]*|`{1,3})\s*/i.test(text)) {
        text = text.replace(/^(?:```[a-zA-Z0-9_-]*|`{1,3})\s*/i, "").trim();
    }

    // 3. Iteratively strip trailing markdown fences or backticks
    while (/\s*(?:```+|`{1,3})$/i.test(text)) {
        text = text.replace(/\s*(?:```+|`{1,3})$/i, "").trim();
    }

    // 4. If there is still a leading prefix before the first HTML tag '<', strip it
    const firstTagIndex = text.indexOf("<");
    if (firstTagIndex > 0) {
        const prefix = text.substring(0, firstTagIndex);
        if (prefix.includes("`") || /html/i.test(prefix) || !prefix.trim()) {
            text = text.substring(firstTagIndex);
        }
    }

    // 5. Strip any trailing non-HTML suffix after the last '>' if it contains backticks
    const lastTagIndex = text.lastIndexOf(">");
    if (lastTagIndex !== -1 && lastTagIndex < text.length - 1) {
        const suffix = text.substring(lastTagIndex + 1);
        if (suffix.includes("`") || !suffix.trim()) {
            text = text.substring(0, lastTagIndex + 1);
        }
    }

    return text.trim();
};

export default {
    date,
    formatDate,
    html,
    newlines,
    cleanString,
    getBoolean,
    quantity,
    price,
    priceFormatter,
    percentage,
    percentageFormatter,
    size,
    videoFormat,
    getFormData,
    cleanHtmlContent,
    getAvatar,
    getInitials,
};
